import { useContext, useMemo, useEffect, useLayoutEffect, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { size, compact } from "lodash"
import { Paper } from "@mui/material"
import { Creators as NotificationCreators } from "modules/notifications/model"
import Error from "components/error"
import Loading from "components/loading"
import NoData from "components/intraday/no-data"
import { usePositionSetMetadata } from "resources/position-sets"
import { useModelMetadata } from "resources/models"
import { useContentSetsMetadata } from "resources/content-sets"
import { useActiveInstruments } from "resources/instruments"
import { useIntradayChannel, useLastIntradayBatch } from "hooks/intraday"
import { combineStatuses, OK, LOADING, ERROR } from "helpers/data-statuses"
import { makeFindAndGetBaseMetadata } from "helpers/base"
import { getIsIntradayFeatureEnabled, getIsIntradayAnalyticsEnabled } from "selectors/features"
import { DashboardContext } from "./context/dashboard"
import useTradeContext from "./context/trade"
import EmptyDashboard from "./components/empty"
import { useInstrumentsWithMetadata } from "./helpers"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "./analytics"
import { DashboardAnalyticsProvider } from "./analytics/provider"
import { useDefaultSelectedBase } from "./hooks/use-selected-base"
import useOpenUnifiedSearch from "./hooks/use-open-unified-search"
import Dashboard from "./dashboard"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import "./style.scss"

function DashboardWrapper() {
  const dispatch = useDispatch()
  const analytics = useAnalytics()
  const { instrument: original, modelId, secondInstrument: rebalanced, comparisonInstrument: comparison, base, isPreferencesLoaded, setBase, isIntradayEnabled, setIsIntradayEnabled, setInstrument, setInstrumentsMetadata, isEndVsStartDateComparison } = useContext(DashboardContext)
  // Create TradeContext at DashboardWrapper level so it persists across Dashboard unmount/remount during loading states
  const [TradeContext, tradeContextValue] = useTradeContext(DashboardContext)
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  const [instrumentMetadata, instrumentStatus] = usePositionSetMetadata(modelId, original, modelMetadata?.availability?.currentDate)
  const [comparisonInstrumentMetadata, comparisonInstrumentStatus] = usePositionSetMetadata(modelId, comparison, modelMetadata?.availability?.currentDate)
  const [baseInstrumentMetadata, baseInstrumentStatus] = usePositionSetMetadata(modelId, base, modelMetadata?.availability?.currentDate)
  // [TODO] What is this?
  // [TODO] Answer: It's combining instruments and instruments metadata.
  const [instrumentsWithMetadata, instrumentsWithMetadataStatus] = useInstrumentsWithMetadata(modelId, modelMetadata?.availability?.currentDate, { original, comparison, rebalanced, base })
  const [contentSets, contentSetsStatus] = useContentSetsMetadata()
  const hasMultipleInstruments = useMemo(() => {
    // In End vs Start mode, original and comparison are the same instrument at different dates
    // so we shouldn't count them as multiple instruments
    if (isEndVsStartDateComparison) {
      return size(compact([original, rebalanced])) > 1
    }
    return size(compact([original, rebalanced, comparison])) > 1
  }, [original, rebalanced, comparison, isEndVsStartDateComparison])
  const [activeInstruments] = useActiveInstruments(modelId)

  const defaultSelectedBase = useDefaultSelectedBase(modelId)

  const status = useMemo(() =>
    combineStatuses([instrumentStatus, baseInstrumentStatus, modelMetadataStatus, comparisonInstrumentStatus, instrumentsWithMetadataStatus, contentSetsStatus]),
  [instrumentStatus, baseInstrumentStatus, modelMetadataStatus, comparisonInstrumentStatus, instrumentsWithMetadataStatus, contentSetsStatus])

  useEffect(() => {
    if (isPreferencesLoaded && modelMetadata && base && activeInstruments && !makeFindAndGetBaseMetadata(null, activeInstruments)(base)) {
      if (defaultSelectedBase && makeFindAndGetBaseMetadata(null, activeInstruments)(defaultSelectedBase)) {
        setBase(
          {
            id: defaultSelectedBase.id,
            type: defaultSelectedBase.type,
          },
          instrumentMetadata,
        )
        dispatch(NotificationCreators.add("warning", null, "A previously saved benchmark is no longer available; this has been replaced by a default benchmark."))
      } else {
        setBase(null)
        dispatch(NotificationCreators.add("warning", null, "The selected benchmark is no longer available."))
      }
    }
  }, [isPreferencesLoaded, setBase, base, activeInstruments, defaultSelectedBase, dispatch, modelMetadata, instrumentMetadata])

  // TODO: This should probably be replaced/updated when Instrument Contexts are implemented
  useEffect(() => {
    setInstrumentsMetadata({
      original: instrumentMetadata,
      comparison: comparisonInstrumentMetadata,
      rebalanced: instrumentsWithMetadata?.rebalanced,
      base: baseInstrumentMetadata,
    })
  }, [instrumentMetadata, comparisonInstrumentMetadata, baseInstrumentMetadata, instrumentsWithMetadata?.rebalanced, setInstrumentsMetadata])

  const isIntradayFeatureEnabled = useSelector(getIsIntradayFeatureEnabled)
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)

  // Use layout instead of useEffect to avoid rendering the page with the wrong mode
  // Enabled intraday if we have the new intraday analytics feature on or if it's an intraday instrument (Intraday Portfolio's for instance)
  useLayoutEffect(() => {
    const isIntradayByInstrument = isIntradayFeatureEnabled && instrumentMetadata?.intradayEnabled
    setIsIntradayEnabled(!!isIntradayByInstrument || !!isIntradayAnalyticsEnabled)
  }, [isIntradayEnabled, instrumentMetadata, setIsIntradayEnabled, isIntradayFeatureEnabled, isIntradayAnalyticsEnabled])

  // we need to set up the intraday channel for the dashboard
  useIntradayChannel(isIntradayEnabled, original?.id, original?.type, "dashboard")
  const [lastIntradayBatch, lastIntradayBatchStatus] = useLastIntradayBatch(original, instrumentMetadata?.intradayEnabled)

  const onMainInstrumentSelection = useCallback(({ id, name, type, searchType, isInitialSelection }) => {
    const payload = { instrument: { id, name, type }, searchType }
    if (isInitialSelection) {
      analytics.track(DASHBOARD_EVENTS.INITIAL_INSTRUMENT_SELECTION, payload)
    } else {
      analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_SELECTION, payload)
    }
  }, [analytics])

  const openMainInstrumentSearch = useOpenUnifiedSearch(DashboardContext, setInstrument, RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD, null, false, false, onMainInstrumentSelection)

  if (status === LOADING) {
    return <Loading />
  }

  if (status === ERROR) {
    analytics.track(SHARED_EVENTS.ERROR, { type: "data_loading_error", message: "Failed to load dashboard data" })

    return <Error />
  }

  if (!isPreferencesLoaded || status !== OK) {
    return <Loading />
  }

  if (!original) {
    return <EmptyDashboard openUnifiedSearch={openMainInstrumentSearch} />
  }

  if (!instrumentMetadata) {
    return <Loading />
  }

  if (isIntradayEnabled && instrumentMetadata?.intradayMetadata && !lastIntradayBatch) {
    return <Loading />
  } else if (isIntradayEnabled && instrumentMetadata?.intradayMetadata && !lastIntradayBatch?.id) {
    return <Paper className="details"><NoData autoRefresh /></Paper>
  }

  return (
    <Dashboard {...{
      modelMetadata,
      context: DashboardContext,
      instrumentMetadata,
      comparisonInstrumentMetadata,
      baseInstrumentMetadata,
      instrumentsWithMetadata,
      contentSets,
      hasMultipleInstruments,
      lastIntradayBatch,
      lastIntradayBatchStatus,
      TradeContext,
      tradeContextValue,
    }}
    />
  )
}

export default function DashboardWrapperWithAnalytics() {
  return (
    <DashboardAnalyticsProvider>
      <DashboardWrapper />
    </DashboardAnalyticsProvider>
  )
}
